import { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui";
import { Edit, Eye, MoreVertical, Trash2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useDeleteJobPostingMutation, useGetJobPostingList } from "@/services";
import { toast } from "sonner";
import moment from "moment";

const useJobPosting = (selectedTab?: string) => {
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [deletedRow, setDeletedRow] = useState<any>(null);
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const onAdd = (tab: string) => {
    navigate(`add`, { state: { applicableCompany: tab } });
  };

  const { data: jobPostingList, isLoading: isJobPostingLoading } =
    useGetJobPostingList({
      data: {
        limit: pageSize,
        orderBy: "updatedAt",
        orderDirection: "ASC",
        page: pageNumber + 1,
        search: {
          applicableCompany: selectedTab =="MBAPL"? "MBAPL" : selectedTab == "KPL"? "KPL" : "OSTWAL",
        }
      }   
    });

  const { mutate: deleteInfo, isPending: isDeletePending } =
    useDeleteJobPostingMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.issues?.error === "string"
            ? error?.response?.data?.issues?.error
            : typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong",
          {
            className: "!bg-red-500 !text-white",
          }
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        toast.success("Job Posting Deleted Successfully", {
          className: "!bg-green-600 !text-white",
        });
        queryClient.invalidateQueries({ queryKey: ["job-posting"] });
        setOpenDialog(false);
      },
    });

  // Define Table Columns
  const columns = [
    // {
    //   accessorKey: "jobId",
    //   header: "Job ID",
    //   accessorFn: (row: any) => String(row.jobId || ""),
    // },
    {
      accessorKey: "jobRole",
      header: "Role",
      cell: (row: any) => (
        <b className="text-primary font-[600]">
          {String(row.row?.original?.jobRole || "")}
        </b>
      ),
    },
    {
      accessorKey: "location",
      header: "Location",
      accessorFn: (row: any) => String(row.location || ""),
    },
    {
      accessorKey: "companyName",
      header: "Company",
      accessorFn: (row: any) => String(row.companyName || ""),
    },
    {
      accessorKey: "createdAt",
      header: "Created On",
      accessorFn: (row: any) =>
        row?.createdAt ? moment(row?.createdAt).format("MM-DD-YYYY") : "",
    },
    {
      id: "actions",
      header: "Actions",
      cell: (row: any) => (
        <DropdownMenu>
          <DropdownMenuTrigger>
            <MoreVertical className="w-5 h-5 cursor-pointer" />
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="bg-white border-0 shadow-md"
          >
            <DropdownMenuItem
              className="flex items-center gap-2"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                navigate(`${row.row.original._id}/view`);
              }}
            >
              <Eye className="w-4 h-4" />
              View
            </DropdownMenuItem>
            <DropdownMenuItem
              className="flex items-center gap-2"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                navigate(`${row.row.original._id}/edit`);
              }}
            >
              <Edit className="w-4 h-4" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem
              className="flex items-center gap-2"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setDeletedRow(row.row.original);
                setOpenDialog(true);
              }}
            >
              <Trash2 className="w-4 h-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  const onDeleteConfirm = () => {
    deleteInfo({ id: deletedRow?._id });
  };

  return {
    onAdd,
    columns,
    openDialog,
    deletedRow,
    setOpenDialog,
    isDeletePending,
    onDeleteConfirm,
    setPageNumber,
    setPageSize,
    pageNumber,
    pageSize,
    jobPostingList,
    isJobPostingLoading,
  };
};

export default useJobPosting;
